import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { INTERPRETER_FAQ_CATEGORIES } from "@/content/interpreter-corpus/faq-defs";

export const Route = createFileRoute("/en/interpreter/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/interpreter-corpus");
    return { faqs: m.INTERPRETER_FAQ_EN };
  },
  component: InterpreterFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Court & Government Interpreters in Thailand — ${faqs.length} FAQs`,
        description:
          "Interpreters for Thai courts, police investigations, district (amphur) registries, immigration offices, land offices, Notarial Services Attorneys, embassies and the Department of Consular Affairs, hospitals and business meetings — plus qualifications, impartiality and how to book.",
        path: "/en/interpreter/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/interpreter/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Court & Government Interpreter FAQ", url: `${site}/en/interpreter/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/interpreter/faq")) },
      ],
    };
  },
});

function InterpreterFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Court &amp; Government Interpreter FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Court, Police and Government Interpreting in Thailand — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          Answers follow section 13 of the Criminal Procedure Code (provision of interpreters during investigation and trial), section 46 of the Civil Procedure Code (language of the court and translation of exhibits), the Office of the Judiciary (coj.go.th), the Bureau of Registration Administration (bora.dopa.go.th), the Immigration Bureau (immigration.go.th), the Department of Lands (dol.go.th), the Lawyers Council of Thailand (lawyerscouncil.or.th), the Department of Consular Affairs (consular.mfa.go.th) and the Personal Data Protection Act B.E. 2562. The Apostille Convention enters into force for Thailand on 28 February 2027. General information, not an official determination.
        </p>
        <section aria-label="Topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {INTERPRETER_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/interpreter/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <FaqSourceNote topic="interpreter" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}